/**
 * WorkflowEditorOverlay - Full-screen overlay for workflow editor
 * Used within AgentModal for multiagent workflow configuration
 */

import React, { useEffect, useCallback } from 'react';
import { XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';
import { ReactFlowProvider } from '@xyflow/react';

import { WorkflowEditor } from '../Workflow/WorkflowEditor';
import { getDefaultWorkflow, type SupportedLanguage } from '../Workflow/WorkflowEditor/defaultWorkflow';
import { useWorkflowStore } from '../../../stores/workflow';
import type { WorkflowDefinition } from '../../../stores/workflow/types';
import { Button } from '../../common';
import { useAdminTranslation } from '../../../modules/i18n';

interface WorkflowEditorOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (definition: Record<string, unknown>) => void;
  initialDefinition?: Record<string, unknown> | null;
  languageCode?: SupportedLanguage;
}

const WorkflowEditorOverlay: React.FC<WorkflowEditorOverlayProps> = ({ isOpen, onClose, onSave, initialDefinition, languageCode = 'vi' }) => {
  const { t } = useAdminTranslation();
  const loadWorkflow = useWorkflowStore((s) => s.loadWorkflow);
  const exportWorkflow = useWorkflowStore((s) => s.exportWorkflow);

  // Load initial definition when overlay opens
  useEffect(() => {
    if (isOpen && initialDefinition) {
      loadWorkflow(initialDefinition as unknown as WorkflowDefinition);
    } else if (isOpen && !initialDefinition) {
      // Load language-specific default KG RAG workflow for new agents
      loadWorkflow(getDefaultWorkflow(languageCode));
    }
  }, [isOpen, initialDefinition, loadWorkflow, languageCode]);

  // Handle save
  const handleSave = useCallback(() => {
    const definition = exportWorkflow();
    onSave(definition as unknown as Record<string, unknown>);
  }, [exportWorkflow, onSave]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className='fixed inset-0 z-[60] flex flex-col bg-gray-50 dark:bg-dark-background'>
      {/* Header */}
      <div className='flex h-14 items-center justify-between border-b border-gray-200 bg-white px-4 dark:border-border-dark dark:bg-dark-background-secondary'>
        <div className='flex items-center gap-3'>
          <button
            onClick={onClose}
            className='rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-dark-text-secondary dark:hover:bg-dark-background-hover dark:hover:text-dark-text'
            aria-label={t('common.close')}
          >
            <XMarkIcon className='h-5 w-5' />
          </button>
          <div>
            <h2 className='text-lg font-semibold text-gray-900 dark:text-dark-text'>{t('content.agents.modal.workflow.editorTitle')}</h2>
            <p className='text-xs text-gray-500 dark:text-dark-text-secondary'>{t('content.agents.modal.workflow.editorSubtitle')}</p>
          </div>
        </div>

        <div className='flex items-center gap-2'>
          <Button type='button' variant='outline' onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type='button' onClick={handleSave} className='bg-purple-600 text-white hover:bg-purple-700'>
            <CheckIcon className='mr-2 h-4 w-4' />
            {t('content.agents.modal.workflow.saveWorkflow')}
          </Button>
        </div>
      </div>

      {/* Workflow Editor */}
      <div className='flex-1 overflow-hidden'>
        <ReactFlowProvider>
          <WorkflowEditor className='h-full w-full' />
        </ReactFlowProvider>
      </div>
    </div>
  );
};

export default WorkflowEditorOverlay;
